import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

// Admins can use everything so the team can test; the backend applies the same rule.
export function useHasPro() {
  const { user } = useAuth();
  return !!(user?.is_pro || user?.role === 'admin');
}

/** Small "Pro" tag shown on locked features. */
export function ProTag({ className = '' }) {
  return (
    <span className={`inline-flex h-5 items-center gap-1 rounded-sm bg-marker px-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-on-marker ${className}`}>
      <Lock className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />Pro
    </span>
  );
}

/**
 * Wrap a Pro-only action. Returns [gate, dialog]: call gate(fn) in a click handler;
 * Pro users run fn, everyone else sees the upgrade dialog. Render {dialog} once.
 */
export function useProGate(featureName) {
  const hasPro = useHasPro();
  const [open, setOpen] = useState(false);

  const gate = (fn) => (...args) => {
    if (hasPro) return fn(...args);
    setOpen(true);
    return undefined;
  };

  const dialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Unlock {featureName}</DialogTitle>
          <DialogDescription>
            Pilot Pro unlocks PDF reports, tenant share links and move-in/move-out comparison for a one-time $15. No subscription.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Not now</Button>
          <Button asChild><Link to="/upgrade">See Pilot Pro</Link></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return [gate, dialog, hasPro, () => setOpen(true)];
}
